/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: badge.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Badge component for displaying status indicators, labels, and
    visual markers throughout the Ring2All Platform interface.

 🔧 Notes:
    - Implements customizable badge variants and sizes
    - Supports different visual styles for status indication
    - Integrates with Tailwind CSS for consistent styling
    - Provides accessibility features and proper contrast
    - Supports custom className and children props

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { ReactNode } from 'react';

interface BadgeProps {
  children: ReactNode;
  variant?: 'default' | 'secondary' | 'destructive' | 'outline';
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function Badge({ children, variant = 'default', size, className = '' }: BadgeProps) {
  const baseClasses = 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium';
  
  const variantClasses = {
    default: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    secondary: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
    destructive: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    outline: 'border border-gray-200 text-gray-800 dark:border-gray-700 dark:text-gray-300'
  };
  
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-0.5',
    lg: 'text-base px-3 py-1'
  };
  
  const sizeClass = size ? sizeClasses[size] : '';
  
  return (
    <span className={`${baseClasses} ${variantClasses[variant]} ${sizeClass} ${className}`}>
      {children}
    </span>
  );
}
